/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
// Vendor
import moment from 'moment';
import { EventEmitter } from 'events';

// Personal libs
import {
  LoggerEventListenerEnum,
  LoggerLevelEnum,
  LoggerLevelEnumTools,
} from './logger.enum';
import { Logger } from './logger.class';
import { LogEntry, LogOptions } from './logger.interface';
import { LoggerStyle } from './logger.style';

export abstract class AbstractLogManager extends EventEmitter {
  protected options: LogOptions = {
    fromServiceWorker: false,
  };
  protected DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss.SSS';

  // Prevent the console logger from being added twice
  protected consoleLoggerRegistered = false;

  // Set level of log in function of environment
  protected consoleLog = process.env.LOGGER?.consoleLog;
  protected envMinLevel = `${process.env.LOGGER?.consoleLog.level}`
    .toString()
    .toUpperCase();

  /**
   *
   * @param options
   * @returns
   */
  public configure(options?: LogOptions): AbstractLogManager {
    if (options != null) {
      this.options = Object.assign({}, this.options, options);
    }
    return this;
  }

  /**
   * Constructor
   * @param module
   * @returns
   */
  public getLogger(): Logger | undefined {
    // Check if the required level exists
    if (
      this.consoleLog.enabled &&
      !Object.keys(LoggerLevelEnum).includes(this.envMinLevel)
    ) {
      console.error(`Unknown log level '${this.envMinLevel}'`);
      return undefined;
    } else {
      return new Logger(
        this,
        LoggerLevelEnum[this.envMinLevel],
        this.options.fromServiceWorker
      );
    }
  }

  /**
   * Manage console entries
   * @param listener
   * @returns
   */
  protected onLogEntry(
    listener: (logEntry: LogEntry) => void
  ): AbstractLogManager {
    super.on(LoggerEventListenerEnum.CONSOLE_LOG, listener);
    return this;
  }

  /**
   * Set if goupr is needed
   * @param {LogEntry} logEntry
   * @returns {boolean}
   */
  protected hasToGroup(logEntry: LogEntry): boolean {
    return (
      typeof console.groupCollapsed === 'function' &&
      logEntry.arguments.length > 1 &&
      typeof logEntry.arguments[0] === 'string'
    );
  }

  /**
   * setArgZero : add information (timestamp, logLevel, title and location) to the text content
   * @param {LogEntry} logEntry
   * @param {any} arg0
   * @returns
   */
  protected setArgZero(logEntry: LogEntry, arg0: string): any {
    let newArg0 = '';

    if (typeof arg0 === 'string') {
      const location = logEntry.location;
      const title = logEntry.title;
      const message = logEntry.message;

      // Agregate the three informations
      if (title != null) {
        newArg0 = `%c${title}%c ${message || arg0}`;
      } else {
        newArg0 = `%c${message || arg0}`;
      }

      // Agregate the three informations
      if (location != null) {
        newArg0 = `${newArg0}%c - (${location})`;
      }

      // Add log level (only on top group line)
      newArg0 = `%c${LoggerLevelEnum[logEntry.level].charAt(0)}%c ${newArg0}`;

      // Add timestamp (DEBUG / TRACE levels)
      if (
        [
          LoggerLevelEnum.TRACE,
          LoggerLevelEnum.DEBUG,
          LoggerLevelEnum.WARN,
          LoggerLevelEnum.ERROR,
          LoggerLevelEnum.FATAL,
        ].includes(logEntry.level)
      ) {
        newArg0 = `${moment().format(this.DATETIME_FORMAT)} - ${newArg0}`;
      }
    }

    return newArg0;
  }

  /**
   * Add style to the output
   * @param arg
   * @param logEntry
   * @returns
   */
  protected addStyleToArg(newArg: any[], logEntry: LogEntry): any[] {
    newArg.push(LoggerStyle.getLevelStyle(logEntry.level));
    newArg.push(LoggerStyle.contentStyle);
    if (logEntry.title != null) {
      newArg.push(LoggerStyle.titleStyle);
    }
    newArg.push(LoggerStyle.contentStyle);
    if (logEntry.location != null) {
      newArg.push(LoggerStyle.locationStyle);
    }

    return newArg;
  }

  /**
   * Displays the message in the console log
   * @param logEntry
   */
  protected displayConsoleLog(logEntry: LogEntry): void {
    if (
      this.consoleLog.enabled &&
      LoggerLevelEnumTools.toNumber(this.envMinLevel) !== LoggerLevelEnum.OFF
    ) {
      const arrArguments = [...logEntry.arguments];
      let newArguments: Array<any> = [];

      // Enable / disable console.groupCollapsed
      const hasToGroup = this.hasToGroup(logEntry);

      // Add information to arg0
      if (typeof arrArguments[0] === 'string') {
        arrArguments[0] = this.setArgZero(logEntry, arrArguments[0]);

        // Enable / disable console.groupCollapsed
        if (hasToGroup) {
          // Add style to message (group header)
          newArguments.push(arrArguments[0]);
          this.addStyleToArg(newArguments, logEntry);

          console.groupCollapsed(...newArguments);
          arrArguments.shift();
        }
      }

      newArguments = [];
      newArguments.push(...arrArguments);

      // Add style to message (content)
      if (typeof arrArguments[0] === 'string') {
        this.addStyleToArg(newArguments, logEntry);
      }

      // Execute console
      switch (logEntry.level) {
        case LoggerLevelEnum.TRACE:
        case LoggerLevelEnum.DEBUG:
        case LoggerLevelEnum.INFO:
        case LoggerLevelEnum.LOG:
          if (!hasToGroup) {
            console.log(...newArguments);
          } else {
            console.log(...arrArguments);
          }
          break;
        case LoggerLevelEnum.WARN:
          if (!hasToGroup) {
            console.warn(...newArguments);
          } else {
            console.warn(...arrArguments);
          }
          break;
        case LoggerLevelEnum.ERROR:
        case LoggerLevelEnum.FATAL:
          if (!hasToGroup) {
            console.error(...newArguments);
          } else {
            console.error(...arrArguments);
          }
          break;
        default:
          console.log(moment().format(this.DATETIME_FORMAT), ...arrArguments);
      }

      // End console.groupCollapsed
      if (hasToGroup) console.groupEnd();
    }
  }

  abstract registerConsoleLogger(): AbstractLogManager;
}
